import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label'; 
import { ForgotPasswordDialog } from './ForgotPasswordDialog';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface AuthFormProps {
  mode: 'signin' | 'signup';
}

export function AuthForm({ mode }: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const rateLimitDelay = 60000; // 1 minute in milliseconds
    let currentError: Error | null = null;

    try {
      if (mode === 'signup') {
        await signUp(email, password);
        toast({
          title: 'Success',
          description: 'Please check your email to verify your account.',
        });
      } else {
        await signIn(email, password);
      }
    } catch (error) {
      currentError = error instanceof Error ? error : new Error('Authentication failed');
      const isRateLimit = error instanceof Error && 
        error.message.includes('rate limit exceeded');
      const isTimeout = error instanceof Error && 
        error.message.includes('timeout');

      toast({
        title: 'Error',
        description: isRateLimit
          ? 'Too many attempts. Please wait a minute before trying again.'
          : isTimeout
          ? 'The server is taking too long to respond. Please try again.'
          : currentError.message,
        variant: 'destructive',
      });

      if (isRateLimit) {
        // Disable the form temporarily
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), rateLimitDelay);
      }
    } finally {
      if (!currentError || !currentError.message.includes('rate limit')) {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          autoComplete={mode === 'signup' ? 'new-email' : 'email'}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
        />
        {mode === 'signin' && (
          <Button
            type="button"
            variant="link"
            className="px-0 text-sm"
            onClick={() => setShowForgotPassword(true)}
          >
            Forgot password?
          </Button>
        )}
      </div>

      <Button 
        type="submit" 
        className="w-full" 
        disabled={isLoading}
      >
        {isLoading ? 'Loading...' : mode === 'signup' ? 'Sign Up' : 'Sign In'}
      </Button>
      </form>

      <ForgotPasswordDialog
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
      />
    </div>
  );
}